import '../css/app.css';

import Alpine from 'alpinejs';

// Theme chrome (U7): the header/footer nav component. Imported here — not
// auto-discovered like a block view.js — because chrome is not a block. Like
// every block module it only REGISTERS its Alpine component (inside an
// `alpine:init` listener); app.js still owns Alpine.start() below.
import './nav.js';

// The search view's live search. Imported here because the search view is a template, not a block.
import './site-search.js';

/*
 * Alpine is the single interactivity runtime (.claude/instructions/
 * interactivity.md). Block logic lives in Alpine.data() components defined
 * in each block's view.js; reference implementation: blocks/containers-accordion/view.js.
 *
 * Start mechanism: this entry and every block view.js are module scripts,
 * which the browser defers — all of them execute, in document order, before
 * DOMContentLoaded fires. Block modules only REGISTER components (inside an
 * `alpine:init` listener); Alpine.start() below is deferred to
 * DOMContentLoaded, so every enqueued block module has registered before
 * Alpine dispatches `alpine:init` and initializes the tree. A view.js must
 * never call Alpine.start() itself.
 */

// Progressive-enhancement marker: CSS that must not apply without JS — e.g.
// the accordion's collapsed-panel rules in blocks/containers-accordion/view.css — is
// scoped to `html.js`. No-JS visitors never get the class, so they always
// see the full server-rendered content.
document.documentElement.classList.add( 'js' );

window.Alpine = Alpine;

// Record Alpine.data() registrations for the dev-mode check below.
const registered = new Set();
const register = Alpine.data.bind( Alpine );
Alpine.data = ( name, callback ) => {
	registered.add( name );
	register( name, callback );
};

/**
 * Dev-only check: an `x-data="component(...)"` whose component was never
 * registered is silent dead UI — Alpine itself only logs a generic
 * expression error. Name the component and the offending element loudly.
 * Compiled out of production builds via Vite's dev flag.
 */
function warnUnregisteredComponents() {
	document.querySelectorAll( '[x-data]' ).forEach( ( root ) => {
		const match = ( root.getAttribute( 'x-data' ) ?? '' ).match( /^\s*([A-Za-z_$][\w$]*)\s*\(/ );

		if ( match && ! registered.has( match[ 1 ] ) ) {
			console.error(
				`Origins: Alpine component "${ match[ 1 ] }" is referenced by x-data but was never registered. ` +
					"Register it with Alpine.data() in the block's view.js (see .claude/instructions/interactivity.md).",
				root
			);
		}
	} );
}

function start() {
	Alpine.start();

	if ( import.meta.env.DEV ) {
		warnUnregisteredComponents();
	}
}

// Module scripts execute at readyState 'interactive', so "not loading" does
// NOT mean later-ordered block modules have run yet. DOMContentLoaded is the
// spec guarantee that every deferred/module script has executed; the
// 'complete' guard covers this entry being injected after the document
// finished loading, when DOMContentLoaded has already fired.
if ( 'complete' === document.readyState ) {
	start();
} else {
	document.addEventListener( 'DOMContentLoaded', start );
}
